import { useStaticQuery, graphql } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { IStaticContentfulImages } from 'src/types/contentful';

export type IProfilePicture = {
  image?: IGatsbyImageData;
  position: {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
  };
  size?: number;
};

const PEOPLE_BACKGROUND_PICTURES_SETTINGS: IProfilePicture[] = [
  {
    position: {
      top: '4%',
      left: '-4%',
    },
    size: 200,
  },
  {
    position: {
      top: '33%',
      left: '3%',
    },
    size: 95,
  },
  {
    position: {
      top: '44%',
      left: '5%',
    },
    size: 120,
  },
  {
    position: {
      top: '14%',
      left: '3%',
    },
    size: 220,
  },
  {
    position: {
      top: '1%',
      left: '18%',
    },
    size: 100,
  },
  {
    position: {
      top: '-5%',
      left: '10%',
    },
    size: 170,
  },
  {
    position: {
      top: '15%',
      left: '15%',
    },
    size: 115,
  },
  {
    position: {
      top: '32%',
      left: '14%',
    },
    size: 140,
  },
  {
    position: {
      bottom: '386%',
      right: '101%',
    },
    size: 90,
  },
  {
    position: {
      bottom: '29%',
      right: '1%',
    },
    size: 140,
  },
  {
    position: {
      bottom: '13%',
      right: '4%',
    },
    size: 135,
  },
  {
    position: {
      bottom: '3%',
      right: '15%',
    },
    size: 80,
  },
  {
    position: {
      bottom: '10%',
      right: '12%',
    },
    size: 110,
  },
  {
    position: {
      bottom: '20%',
      right: '12%',
    },
    size: 160,
  },
  {
    position: {
      bottom: '33%',
      right: '12%',
    },
    size: 60,
  },
  {
    position: {
      bottom: '-4%',
      right: '5%',
    },
    size: 120,
  },
  {
    position: {
      bottom: '2%',
      right: '2%',
    },
    size: 90,
  },
];

const PEOPLE_BACKGROUND_PICTURES_SETTINGS_MOBILE: IProfilePicture[] = [
  {
    position: {
      top: '5%',
      left: '-3%',
    },
    size: 50,
  },
  {
    position: {
      top: '17%',
      left: '-9%',
    },
    size: 75,
  },
  {
    position: {
      top: '-2%',
      left: '2%',
    },
    size: 100,
  },
  {
    position: {
      top: '-1%',
      left: '30%',
    },
    size: 40,
  },
  {
    position: {
      top: '12%',
      left: '18%',
    },
    size: 70,
  },
  {
    position: {
      bottom: '3%',
      right: '-4%',
    },
    size: 50,
  },
  {
    position: {
      bottom: '-8%',
      right: '8%',
    },
    size: 85,
  },
  {
    position: {
      bottom: '10%',
      right: '9%',
    },
    size: 120,
  },
  {
    position: {
      bottom: '22%',
      right: '8%',
    },
    size: 30,
  },
  {
    position: {
      bottom: '18%',
      right: '-9%',
    },
    size: 70,
  },
  {
    position: {
      bottom: '37%',
      right: '-3%',
    },
    size: 50,
  },
];

const usePeopleProfileBackground = (mobile = false): IProfilePicture[] => {
  const profilePicturesAssets = useStaticQuery<IStaticContentfulImages>(graphql`
    {
      profile1: contentfulAsset(contentful_id: { eq: "1cLIghgPsUfZuoLfo3zz3O" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile2: contentfulAsset(contentful_id: { eq: "1ArkpoMcPVtGtSndF6jATB" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile3: contentfulAsset(contentful_id: { eq: "4LRxRUXc5u79T5lfMv6ORE" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile4: contentfulAsset(contentful_id: { eq: "3ekCcvx97y0yqYs5zyDeZy" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile5: contentfulAsset(contentful_id: { eq: "3btglarM5tQlBr15uoSdD3" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile6: contentfulAsset(contentful_id: { eq: "4yplVusW0EStVLzV2tIvZs" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile7: contentfulAsset(contentful_id: { eq: "1PaUiPBo0qgfkXlFn96XU5" }) {
        gatsbyImageData(width: 500, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile8: contentfulAsset(contentful_id: { eq: "7o5g5FxIXcUXv71cH4a65C" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile9: contentfulAsset(contentful_id: { eq: "6nOyTzqgBIUlfED4TRiXVv" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile10: contentfulAsset(contentful_id: { eq: "5ppVHQDo538UnQNciRLVfv" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile11: contentfulAsset(contentful_id: { eq: "6sPwszbUpV3Dv1EiEozg6p" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile12: contentfulAsset(contentful_id: { eq: "6mNIcU4HJcwu7CZgAJXVrO" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile13: contentfulAsset(contentful_id: { eq: "3gOfiV7utQrRTbTvpr11GR" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile14: contentfulAsset(contentful_id: { eq: "3rWFW0h13gvD0dYNSQLjN9" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile15: contentfulAsset(contentful_id: { eq: "XfxMohVgM7VS6qAjn1ioz" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile16: contentfulAsset(contentful_id: { eq: "3aSlzFgNGXyhrMudhp7wh" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
      profile17: contentfulAsset(contentful_id: { eq: "23OPBCeIrdYXYGzX7fJ52S" }) {
        gatsbyImageData(width: 300, layout: CONSTRAINED, placeholder: BLURRED)
      }
    }
  `);

  const entries = mobile ? Object.entries(profilePicturesAssets).splice(0, 11) : Object.entries(profilePicturesAssets);
  const arr = mobile ? PEOPLE_BACKGROUND_PICTURES_SETTINGS_MOBILE : PEOPLE_BACKGROUND_PICTURES_SETTINGS;
  return entries.map((asset, index) => ({
    image: asset[1].gatsbyImageData as IGatsbyImageData,
    ...arr[index],
  }));
};

export default usePeopleProfileBackground;
